import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import image1 from '../../assets/image_1.png';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

export default function Hero() {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-[70vh] overflow-visible">
      {/* Background Patterns */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-secondary/5" />
        <div 
          className="absolute inset-0 opacity-[0.15]" 
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, ${hexToRgba('#1A2330', 0.2)} 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />
      </div>
      
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-8 lg:px-12 h-full py-6 lg:py-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center h-full">
          
          {/* Left Content Block */}
          <motion.div 
            className="lg:col-span-7 space-y-5 lg:space-y-6 relative z-10"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Headline with translations */}
            <motion.div 
              className="space-y-2 relative"
              variants={itemVariants}
            >
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-primary 
                tracking-tight leading-[1.1] relative max-w-full overflow-hidden">
                <span className="block transform hover:translate-x-2 transition-transform duration-300 whitespace-normal">
                  {t('hero.headline.line1')}
                </span>
                <span className="block transform hover:translate-x-2 transition-transform duration-300 whitespace-normal">
                  {t('hero.headline.line2')}
                </span>
                <span className="block text-secondary mt-2 transform hover:translate-x-2 transition-transform duration-300 whitespace-normal">
                  {t('hero.headline.line3')}
                </span>
              </h1>
            </motion.div>

            {/* Mobile Image */}
            <motion.div 
              className="lg:hidden relative -mx-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="relative transform rotate-2 hover:rotate-0 
                transition-transform duration-500">
                <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden
                  aspect-[4/3]">
                  <div className="absolute inset-0 bg-gradient-to-tr 
                    from-white/0 via-white/0 to-secondary/10" />
                  <div className="relative p-8 flex items-center justify-center">
                    <img 
                      src={image1} 
                      alt="Conference Keychain Preview" 
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Subheadline with translations */}
            <motion.div 
              className="space-y-4 max-w-2xl relative pl-6 border-l-2 border-secondary/30"
              variants={itemVariants}
            >
              <p className="text-xl md:text-2xl text-primary/90 font-medium leading-tight">
                {t('hero.subheadline.main')}
              </p>
              <p className="text-lg md:text-xl text-text/80 leading-relaxed">
                {t('hero.subheadline.secondary')}
              </p>
            </motion.div>
            
            {/* CTA Button and Pricing with translations */}
            <motion.div 
              className="pt-4"
              variants={itemVariants}
            >
              <div className="space-y-4">
                <a 
                  href="/#quote-request" 
                  onClick={(e) => {
                    if (window.location.pathname === '/') {
                      e.preventDefault();
                      document.getElementById('quote-request')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-8 py-4 bg-primary text-white rounded-lg 
                    hover:bg-primary-light transition-all duration-300 
                    shadow-lg hover:shadow-xl text-lg font-medium
                    transform hover:-translate-y-0.5"
                >
                  {t('hero.cta.button')}
                </a>

                <div className="text-text/70">
                  <span className="font-medium">{t('hero.cta.pricing.main')}</span>
                  <span className="text-sm block mt-1">{t('hero.cta.pricing.details')}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Desktop Image - hidden on mobile */}
          <motion.div 
            className="hidden lg:block lg:col-span-5 relative -mr-4 lg:-mr-8"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {/* Decorative Background Elements */}
            <div className="absolute -inset-4 bg-gradient-to-br 
              from-secondary/20 via-primary/10 to-transparent 
              rounded-3xl blur-2xl" />
            
            {/* Connecting Lines */}
            <div className="absolute -inset-4 overflow-hidden">
              <div className="absolute top-1/4 left-0 w-16 h-[2px] bg-secondary/20 
                transform -rotate-45" />
              <div className="absolute bottom-1/4 right-0 w-16 h-[2px] bg-secondary/20 
                transform rotate-45" />
            </div>
            
            {/* Render Container */}
            <div className="relative transform rotate-2 hover:rotate-0 
              transition-transform duration-500 scale-110">
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden
                aspect-[4/3]">
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr 
                  from-white/0 via-white/0 to-secondary/10" />
                
                {/* Render Image */}
                <div className="relative p-8 flex items-center justify-center">
                  <img 
                    src={image1} 
                    alt="Conference Keychain Preview" 
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Sponsor Box - Now below the image */}
          <motion.div 
            className="hidden lg:block lg:col-start-8 lg:col-span-5 -mt-10"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="px-6 py-4 bg-secondary/5 border border-secondary/20 rounded-lg
              hover:bg-secondary/10 transition-colors duration-300">
              <p className="text-sm font-medium text-secondary">
                {t('hero.sponsor.title')}
              </p>
              <p className="text-sm text-text/70 mt-1">
                {t('hero.sponsor.description')}
              </p>
            </div>
          </motion.div>
          
        </div>
      </div>
    </div>
  );
}

// Utility function for background dots
function hexToRgba(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

